import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import {
  completeAssetRequest,
  fetchAssetRequests,
  rejectAssetRequest,
} from "services/asset-requests.service";
import { canManageOnboarding } from "utils/onboardingPermissions";
import { errorCode } from "features/positions/positions-errors";
import { AssetRequest, AssetRequestStatus } from "types/AssetRequest";
import AssetsTabs from "./AssetsTabs";
import AssetRequestsTable from "./AssetRequestsTable";
import NewRequestDialog from "./NewRequestDialog";
import PickAssetDialog from "./PickAssetDialog";
import ResolveDialog from "./ResolveDialog";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { describeAssetRequestError } from "./asset-requests-helpers";

const FILTERS: AssetRequestStatus[] = ["pending", "done", "rejected"];

/**
 * Turtas → «Prašymai»: the queue of equipment people ask for (route
 * /app/:companyId/assets/requests, Master | Access assets). Requests fall in
 * from the hiring flow, from colleagues and from employees themselves; the
 * issuer hands the equipment out, then closes the request.
 */
export default function AssetRequestsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.authReducer.role);

  const [status, setStatus] = useState<AssetRequestStatus>("pending");
  const [requests, setRequests] = useState<AssetRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  const [creating, setCreating] = useState(false);
  const [issuingFor, setIssuingFor] = useState<AssetRequest | null>(null);
  const [resolving, setResolving] = useState<{ request: AssetRequest; kind: "complete" | "reject" } | null>(null);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    fetchAssetRequests(companyId, status)
      .then((rows) => {
        if (!cancelled) setRequests(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, status, reloadCount]);

  if (!companyId) return null;

  /** After any change: reload the list and the number on the menu and the tab. */
  const refresh = () => {
    setReloadCount((count) => count + 1);
    dispatch(fetchCabinetSummaryThunk(companyId));
  };

  const changeStatus = (next: AssetRequestStatus) => {
    if (next === status) return;
    setRequests([]);
    setStatus(next);
  };

  const resolve = async (text: string) => {
    if (!resolving) return;
    const { request, kind } = resolving;
    try {
      if (kind === "complete") await completeAssetRequest(companyId, request.id, text);
      else await rejectAssetRequest(companyId, request.id, text);
      setResolving(null);
      refresh();
    } catch (e) {
      toast.error(describeAssetRequestError(e, t));
      // Someone else closed it first, or it is gone: nothing left to do here but show the new state.
      const code = errorCode(e);
      if (code === "REQUEST_NOT_PENDING" || code === "NOT_FOUND") {
        setResolving(null);
        refresh();
      }
    }
  };

  const openAssignForm = (assetId: string, employeeId: string) => {
    setIssuingFor(null);
    dispatch(setModalxComponent("UserAssignToAssetsForm"));
    dispatch(setModalxComponentProps({ id: assetId, presetUserId: employeeId }));
  };

  const renderList = () => {
    if (loadFailed) {
      return (
        <div className="flex items-center gap-3 px-4 pb-4 text-sm text-red-700">
          {t("asset_requests_load_failed")}
          <button type="button" className="underline" onClick={() => setReloadCount((count) => count + 1)}>
            {t("retry")}
          </button>
        </div>
      );
    }
    if (loading && requests.length === 0) {
      return <p className="px-4 pb-4 text-sm text-gray-500">{t("loading")}</p>;
    }
    if (requests.length === 0) {
      return <p className="px-4 pb-4 text-sm text-gray-500">{t(`asset_requests_empty_${status}`)}</p>;
    }
    return (
      <AssetRequestsTable
        companyId={companyId}
        requests={requests}
        status={status}
        canOpenOnboarding={canManageOnboarding(role)}
        onIssue={setIssuingFor}
        onComplete={(request) => setResolving({ request, kind: "complete" })}
        onReject={(request) => setResolving({ request, kind: "reject" })}
      />
    );
  };

  return (
    <div className="w-full px-6 py-4">
      <AssetsTabs companyId={companyId} />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("asset_requests_title")}</h1>
          <p className="mt-0.5 text-sm text-gray-500">{t("asset_requests_page_hint")}</p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          onClick={() => setCreating(true)}
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t("asset_requests_new")}
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap gap-2 p-4" role="group" aria-label={t("status")}>
          {FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              aria-pressed={filter === status}
              onClick={() => changeStatus(filter)}
              className={clsx(
                "rounded-full border px-3 py-1 text-sm font-medium",
                filter === status
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              )}
            >
              {t(`asset_requests_filter_${filter}`)}
            </button>
          ))}
        </div>

        {renderList()}
      </div>

      {creating && (
        <NewRequestDialog
          companyId={companyId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {issuingFor && (
        <PickAssetDialog
          companyId={companyId}
          employeeName={issuingFor.employee.name ?? "—"}
          onPicked={(assetId) => openAssignForm(assetId, issuingFor.employee.id)}
          onClose={() => setIssuingFor(null)}
        />
      )}
      {resolving && (
        <ResolveDialog
          request={resolving.request}
          kind={resolving.kind}
          onSubmit={resolve}
          onClose={() => setResolving(null)}
        />
      )}
    </div>
  );
}
